import React from 'react';
import { Route, Switch } from 'react-router-dom';
import { styled } from '@mui/material';
import { useAppSelector, useLocation } from 'src/utils/Hooks';
import { LoginContainerConfigs } from 'src/components/SignIn/SignInContainer';
import { MarketingAuthRoutes, MarketingSiteContainer, MarketingIframe } from 'src/routes/routeTable_v2';
import { getEnterpriseSsoHost, getSsoReferrer } from 'src/utils/ssoReferrer';

const SignInV2Container = React.lazy(() => import('src/components/SignIn/SignInV2Container'));
const AuthCallback = React.lazy(() => import('src/components/SignIn/AuthCallback'));
const SignOut = React.lazy(() => import('src/components/SignOut/SignOut_v2'));
const SwitchDivision = React.lazy(() => import('src/components/SignIn/SwitchDivision'));
const NoAccessRedirect = React.lazy(() => import('src/components/SignIn/NoAccessRedirect'));

const Root = styled('div')({
  height: '100%'
});

/**
 * Lightweight, V1/V2-agnostic shell for auth routes.
 *
 * Mounted by `main.tsx` for the seven auth paths before any V1/V2 shell
 * decision is made. The decision is deferred until after the form submits
 * (we now know the email and Split.io can decide). The submit handler
 * persists the result via `setAuthMethod(...)` and triggers a full reload,
 * which remounts `main.tsx` -> not an auth route -> AppSwitch picks the
 * correct heavy shell on its first evaluation.
 *
 * Rendering rules:
 * - `/sign_in` on app domain -> redirect to enterprise-sso/sign_in
 * - `/sign_in` on enterprise-sso -> SignInV2Container (with marketing iframe)
 * - `/switch-division` and `/no-access` -> redirect to enterprise-sso when
 *   accessed from app domain; render directly on enterprise-sso
 * - Everything else -> reuses the existing components from routeTable_v2
 */
const AuthShell: React.FC = () => {
  const location = useLocation();
  const app = useAppSelector((state) => state.app);
  const isEnterpriseSso = window.location.host.includes('enterprise-sso');

  // App-domain pre-login: kick the user to the unified enterprise-sso login.
  if (!isEnterpriseSso && location.pathname === '/sign_in') {
    const referrer = getSsoReferrer();
    const returnUrl = encodeURIComponent(window.location.origin + '/');
    window.location.href = `https://${getEnterpriseSsoHost()}/sign_in?referrer=${referrer}&returnUrl=${returnUrl}`;
    return null;
  }

  return (
    <Root>
      <React.Suspense fallback={null}>
        <Switch>
          <Route
            exact
            path="/sign_in"
            render={(renderProps) => {
              if (isEnterpriseSso) {
                const backgroundColor = LoginContainerConfigs?.[app.name]?.bgColor;
                return (
                  <div style={{ display: 'flex', height: '100%' }}>
                    <MarketingSiteContainer className="signin_marketing">
                      <MarketingIframe />
                    </MarketingSiteContainer>
                    <div className="signin_container" style={{ flex: 1, minWidth: '50%', zIndex: 0, backgroundColor }}>
                      <SignInV2Container {...renderProps} />
                    </div>
                  </div>
                );
              }
              return <MarketingAuthRoutes {...renderProps} app={app} />;
            }}
          />

          <Route exact path="/auth/callback" component={AuthCallback} />

          <Route
            exact
            path="/password_reset"
            render={(renderProps) => <MarketingAuthRoutes {...renderProps} app={app} />}
          />
          <Route
            exact
            path="/sign_up_invite"
            render={(renderProps) => <MarketingAuthRoutes {...renderProps} app={app} />}
          />

          <Route exact path="/sign_out" component={SignOut} />

          <Route
            exact
            path="/switch-division"
            render={(renderProps) => {
              if (isEnterpriseSso) {
                const backgroundColor = LoginContainerConfigs?.[app.name]?.bgColor;
                return (
                  <div style={{ display: 'flex', height: '100%' }}>
                    <MarketingSiteContainer className="signin_marketing">
                      <MarketingIframe />
                    </MarketingSiteContainer>
                    <div className="signin_container" style={{ flex: 1, minWidth: '50%', zIndex: 0, backgroundColor }}>
                      <SwitchDivision {...renderProps} />
                    </div>
                  </div>
                );
              }
              window.location.href = `https://${getEnterpriseSsoHost()}/switch-division${window.location.search}`;
              return null;
            }}
          />

          <Route
            exact
            path="/no-access"
            render={(renderProps) => {
              if (isEnterpriseSso) {
                return <NoAccessRedirect {...renderProps} />;
              }
              window.location.href = `https://${getEnterpriseSsoHost()}/no-access${window.location.search}`;
              return null;
            }}
          />
        </Switch>
      </React.Suspense>
    </Root>
  );
};

export default AuthShell;
